// ==========================================
// BB's habitat (3b)
// ==========================================
// The one place gamification goes full-panel. Slides up over chat; the back arrow
// returns to the exact scroll position it left. Level, streak calendar, weekly
// challenge and the badge grid all live here rather than in permanent chrome.
// ==========================================

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { Badge, BbState, BuddyProgress, HabitatProp, StreakDay } from '../models/buddy.types';
import { ACCENT_HEX, ACCENT_INK, HABITAT_PROPS } from '../models/buddy.constants';

@Component({
  selector: 'app-buddy-habitat',
  standalone: false,
  templateUrl: './buddy-habitat.component.html',
  styleUrls: ['./buddy-habitat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyHabitatComponent {
  @Input() progress!: BuddyProgress;
  @Input() bbState: BbState = 'idle';

  @Output() closed = new EventEmitter<void>();

  /** Locked badges reveal their unlock condition on tap (3b). */
  revealedBadgeId: string | null = null;

  get xpPct(): number {
    if (!this.progress.xpForLevel) {
      return 0;
    }
    return Math.min(100, Math.round((this.progress.xpIntoLevel / this.progress.xpForLevel) * 100));
  }

  get challengePct(): number {
    const c = this.progress.challenge;
    return c.total ? Math.round((c.done / c.total) * 100) : 0;
  }

  get unlockedBadges(): number {
    return this.progress.badges.filter((b) => b.unlocked).length;
  }

  /** The badge grid shows the first six; the rest live behind the count. */
  get badgeGrid(): readonly Badge[] {
    return this.progress.badges.slice(0, 6);
  }

  get unlockedProps(): readonly HabitatProp[] {
    return this.progress.habitat.filter((p) => p.unlocked);
  }

  get nextPropLevel(): number | null {
    const next = this.progress.habitat.find((p) => !p.unlocked);
    return next ? next.unlockLevel : null;
  }

  get habitatLine(): string {
    const total = HABITAT_PROPS.length;
    const next = this.nextPropLevel;
    const head = `habitat ${this.unlockedProps.length}/${total} unlocked`;
    return next === null ? `${head} · all found` : `${head} · next at Lv ${next}`;
  }

  get nextUnlockLine(): string {
    const next = this.progress.habitat.find((p) => !p.unlocked);
    return next ? `Next: ${next.label.toLowerCase()} joins the habitat` : 'Every habitat prop unlocked';
  }

  get freezeLine(): string {
    return this.progress.week.some((d) => d.state === 'frozen')
      ? 'Thursday used your monthly freeze — streak intact'
      : this.progress.freezeAvailable
      ? 'One streak freeze banked for this month'
      : 'Streak freeze used for this month';
  }

  hasProp(id: string): boolean {
    return this.progress.habitat.some((p) => p.id === id && p.unlocked);
  }

  /** Glyph colour — the ink variant, since a badge glyph is small text on white. */
  accentFor(badge: Badge): string {
    return ACCENT_INK[badge.accent];
  }

  /** Soft wash behind an unlocked badge, from the same accent's fill tone. */
  tintFor(badge: Badge): string {
    return this.rgba(ACCENT_HEX[badge.accent], 0.12);
  }

  private rgba(hex: string, alpha: number): string {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  toggleBadge(badge: Badge): void {
    if (badge.unlocked) {
      this.revealedBadgeId = null;
      return;
    }
    this.revealedBadgeId = this.revealedBadgeId === badge.id ? null : badge.id;
  }

  get revealedCondition(): string | null {
    const badge = this.progress.badges.find((b) => b.id === this.revealedBadgeId);
    return badge ? `${badge.label} — ${badge.condition}` : null;
  }

  trackBadge(_: number, badge: Badge): string {
    return badge.id;
  }

  trackDay(index: number, _day: StreakDay): number {
    return index;
  }
}
